/**
 * Create or delete venue blocks for tournament matches so the venue is blocked during match time.
 * Blocks prevent double-booking and show as blocked (red) on the venue Gantt alongside manual blocks.
 */

import { doc, addDoc, deleteDoc, collection } from 'firebase/firestore';
import { Timestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import { toUtcMs } from '../utils/dateTimeUtils';

function toDate(val) {
  const ms = toUtcMs(val);
  return ms != null ? new Date(ms) : null;
}

/**
 * Compute start and end Date for a match slot.
 * @param {object} match - { scheduledDate, scheduledTime }
 * @param {number} durationMins - match duration in minutes
 * @returns {{ start: Date, end: Date } | null}
 */
function getMatchSlotDates(match, durationMins = 30) {
  const d = toDate(match.scheduledDate);
  const timeStr = (match.scheduledTime || '').trim() || '00:00';
  if (!d) return null;
  const [hours, minutes] = timeStr.split(':').map((n) => parseInt(n, 10) || 0);
  const start = new Date(d.getFullYear(), d.getMonth(), d.getDate(), hours, minutes, 0, 0);
  const end = new Date(start.getTime() + durationMins * 60 * 1000);
  return { start, end };
}

/**
 * Create or update a venue block for a tournament match. Call after creating or rescheduling a match.
 * If match.venueBlockId exists, the old block is deleted first.
 * @param {object} match - { id, venueId, courtId, scheduledDate, scheduledTime, venueBlockId? }
 * @param {number} durationMins - match duration (default 30)
 * @returns {Promise<string|null>} New block id, or null if no venue/court/date
 */
export async function ensureVenueBlockForMatch(match, durationMins = 30) {
  if (!match?.id || !match?.venueId) return null;
  const slot = getMatchSlotDates(match, durationMins);
  if (!slot) return null;

  if (match.venueBlockId) {
    try {
      await deleteDoc(doc(db, 'venueBlocks', match.venueBlockId));
    } catch (e) {
      console.warn('[venueBlockService] Delete old block failed:', e?.message);
    }
  }

  const ref = await addDoc(collection(db, 'venueBlocks'), {
    venueId: match.venueId,
    courtId: match.courtId || null,
    startTime: Timestamp.fromDate(slot.start),
    endTime: Timestamp.fromDate(slot.end),
    reason: 'Tournament match',
    matchId: match.id,
  });
  return ref.id;
}

/**
 * Delete the venue block for a match. Call when a match is deleted or no longer has venue/date.
 * @param {object} match - { venueBlockId? }
 */
export async function deleteVenueBlockForMatch(match) {
  if (!match?.venueBlockId) return;
  try {
    await deleteDoc(doc(db, 'venueBlocks', match.venueBlockId));
  } catch (e) {
    console.warn('[venueBlockService] Delete block failed:', e?.message);
  }
}
